<script setup>
import EnableModuleAction from "@frontJs/components/actions/EnableModuleAction.vue";
import ModuleDetailsCell from "@frontJs/components/tables/cells/tenantReports/ModuleDetailsCell.vue";
import { CheckCircleIcon } from "@heroicons/vue/24/outline";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { type } = defineProps({
  type: {
    type: String,
    required: false,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  globalData,
  selectionIds,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href:
            route("front.projectManagement.customer.updateCustomer", {
              id: props.row.original.id,
            }) + "?tab=modules",
          target: "_self",
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "enabledModuleCount",
      header: () => t("label.enabledModuleCount"),
      cell: (props) =>
        h(TextCellV2, {
          text:
            props.row.original.enabledModuleCount +
            "/" +
            props.row.original.modules.length,
          displayAsNumber: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "enabledExternalServiceCount",
      header: () => t("label.enabledExternalServiceCount"),
      cell: (props) =>
        h(TextCellV2, {
          text:
            props.row.original.enabledExternalServiceCount +
            "/" +
            props.row.original.externalServices.length,
          displayAsNumber: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "userCount",
      header: () => t("label.userCount"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.userCount,
          color: props.row.original.userCount === 0 ? "red" : undefined,
        }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (props) =>
        h(ModuleDetailsCell, {
          row: props.row,
        }),
    }),
  ],
  url: route("front.postModules", { tenantReport: true }),
  data: {
    type,
  },
  columnPinning: {
    right: ["actions"],
  },
  selectionIdKey: "id",
});

function enableCustomerModuleActionCallback() {
  toast.success(t("toast.successfullyEnabled"), {
    position: "top-right",
  });

  updateData();
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <EnableModuleAction
        :modules="globalData.modules"
        :tenant-ids="selectionIds"
        :callback="enableCustomerModuleActionCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            :left-icon="CheckCircleIcon"
            :callback="() => setIsOpen(true)"
            :disabled="selectionIds.length === 0"
          >
            {{ t("button.enableModule") }}
          </ActionButton>
        </template>
      </EnableModuleAction>
    </template>
  </DataTableLayout>
</template>
